<template>
  <!-- This component does not render anything -->
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { initializePendo } from '@js/utils/pendoHelper'
import { getCustomerProfile } from '@js/api/customer/getCustomerProfile'
import useAuth from '@js/composables/useAuth'

defineOptions({
  name: 'GlobalEvents',
})

const { getAuthUser, isLoggedIn } = useAuth()
const pendoAccountId = 77
const pendoAccountName = 'WCEI'
const pendoInitialized = ref(false)

let adaStarted = (window as any).__adaStarted === true

function startAdaOnce(visitor: { id: string; email: string; full_name: string }) {
  (window as any).adaSettings = (window as any).adaSettings || {}
  ;(window as any).adaSettings.metaFields = { ...(window as any).adaSettings.metaFields, ...visitor }

  // if SDK is ready and not started -> start with meta
  if (!adaStarted && (window as any)._adaReady && (window as any).adaEmbed?.start) {
    (window as any).adaEmbed.start({ handle: 'relias-woundcare', metaFields: visitor })
    adaStarted = true
    ;(window as any).__adaStarted = true
    return
  }

  // if already started -> just update meta
  if ((window as any).adaEmbed?.setMetaFields) {
    (window as any).adaEmbed.setMetaFields(visitor)
  }
}

/**
 * Handles setting Ada meta fields.
 */
function setAdaMeta(meta: { id?: string; email?: string | null; full_name?: string | null }) {
  if (window.updateAdaMeta) {
    window.updateAdaMeta(meta)
  } else {
    ;(window as any).adaSettings = (window as any).adaSettings || {}
    ;(window as any).adaSettings.metaFields = Object.assign(
      {},
      (window as any).adaSettings.metaFields || {},
      meta || {}
    )
  }
}

/**
 * Handles syncing user analytics data with third party services.
 */
async function syncAnalytics() {
  const user = getAuthUser()
  const isUserLoggedIn = Boolean(isLoggedIn.value && user)

  const profile = isUserLoggedIn
    ? await getCustomerProfile(Number(user!.id)).catch(() => null)
    : null

  // Safe coercion to strings with fallbacks
  const coerce = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v))

  const email = coerce(profile?.email ?? (user as any)?.email ?? '')
  const fullName = coerce(
    (profile?.first_name && profile?.last_name)
      ? `${profile.first_name} ${profile.last_name}`
      : (
          (user as any)?.fullName ??
          (user as any)?.friendlyName ??
          [
            (user as any)?.firstName ?? (user as any)?.firstname ?? '',
            (user as any)?.lastName ?? (user as any)?.lastname ?? '',
          ].filter(Boolean).join(' ')
        )
  )
  const visitor = {
    id: coerce(user?.id ?? ''),
    email,
    full_name: fullName,
  }
  const account = {
    id: pendoAccountId,
    name: pendoAccountName,
  }

  if (!pendoInitialized.value) {
    initializePendo(visitor, account)
    pendoInitialized.value = true
  }

  setAdaMeta({
    id: coerce(isUserLoggedIn ? user!.id : ''),
    email,
    full_name: fullName,
  })

  startAdaOnce(visitor)
}

window.addEventListener('ada:ready', () => { void syncAnalytics() }, { once: true })

// Re-run when login state changes
watch(isLoggedIn, () => { void syncAnalytics() })
onMounted(syncAnalytics)
</script>